import { SmartCartBlockType } from '../../smart-cart-block.model';
import { IconSize, TitleMode, TopBarPadding } from '../../widget-editors/top-bar/top-bar.model';
import {
  CART_ITEM_IMAGE_DEFAULTS,
  QuantityInputType,
  SubscriptionSelectorType,
} from '../../widget-editors/cart-items/cart-items.model';
import { TimerFormat } from '../../components/timer-config/timer.model';

export { CART_ITEM_IMAGE_DEFAULTS, QuantityInputType, SubscriptionSelectorType };

export type PreviewSectionId = 'header' | 'body' | 'footer';

export interface PreviewWidget {
  id: string;
  type: SmartCartBlockType;
  enabled: boolean;
  settings: Record<string, unknown>;
}

export interface SmartCartPreviewConfig {
  global: Record<string, unknown>;
  sections: Record<PreviewSectionId, PreviewWidget[]>;
}

export interface TopBarSettings {
  backgroundColor: string;
  // Optional: carts saved before the Padding control existed have no value for it.
  padding?: TopBarPadding;
  bottomBorder: {
    enabled: boolean;
    color: string;
  };
  title: {
    mode: TitleMode;
    text: string;
    logoUrl: string;
    /** Empty means inherit the theme's colour, mirroring the storefront. */
    textColor?: string;
  };
  closeButton: {
    iconColor: string;
    backgroundColor: string;
    hoverColor: string;
    iconSize: IconSize;
  };
  customCode: {
    enabled: boolean;
    code: string;
  };
}

export interface CartItemsSettings {
  emptyCartText: string;
  /** Absent falls back to the colour the block has always used, mirroring the storefront. */
  emptyCartTextColor?: string;
  image?: { fit: 'cover' | 'contain'; width: number; height: number; backgroundColor?: string };
  quantityInput: {
    min: number;
    max: number;
    inputType: QuantityInputType;
    minQuantityMessage: string;
    maxQuantityMessage: string;
    position?: 'bottom' | 'inline';
  };
  subscription: {
    enabled: boolean;
    selectorType: SubscriptionSelectorType;
    label: string;
    upgradingLabel: string;
    // null on a design field = inherit from global.buttonDesign at render time.
    design: {
      backgroundColor: string | null;
      textColor: string | null;
      hoverColor: string | null;
      iconColor: string | null;
      cornerRadius: number | null;
    };
  };
  changeVariant: boolean;
  customCode: {
    enabled: boolean;
    code: string;
  };
}

// Subscription/Checkout/Discount widget `hoverColor` resolves from this `hoverTextColor` field.
export interface GlobalButtonDesign {
  backgroundColor: string;
  textColor: string;
  hoverTextColor: string;
  iconColor: string;
  cornerRadius: number;
}

export const GLOBAL_BUTTON_DESIGN_DEFAULT: GlobalButtonDesign = {
  backgroundColor: '#000000',
  textColor: '#FFFFFF',
  hoverTextColor: '#000000',
  iconColor: '#FFFFFF',
  cornerRadius: 0,
};

export interface CartSubtotalSettings {
  singleItemLabel: string;
  multipleItemLabel: string;
  customCode: {
    enabled: boolean;
    code: string;
  };
}

export interface CheckoutButtonDesign {
  backgroundColor: string | null;
  textColor: string | null;
  hoverColor: string | null;
  cornerRadius: number | null;
}

export interface AnnouncementBarSettings {
  height: 'slim' | 'normal' | 'wide';
  backgroundColor: string;
  border: {
    enabled: boolean;
    color: string;
  };
  messages: { html: string; textColor?: string }[];
  messageRotation: {
    value: number;
    unit: 'seconds' | 'minutes';
  };
  timer: {
    enabled: boolean;
    duration: {
      value: number;
      unit: 'minutes' | 'hours';
    };
    format: 'digital' | 'compact';
    expirationBehavior: 'show_expired_message' | 'hide_message';
    expiredMessage: string;
  };
  customCode: {
    enabled: boolean;
    code: string;
  };
}

export interface DiscountCodesSettings {
  discountCodesEnabled: boolean;
  giftCardCodesEnabled: boolean;
  title: string;
  headerLabel: string;
  buttonLabel: string;
  design: CheckoutButtonDesign;
  invalidMessage: string;
  customCode: {
    enabled: boolean;
    code: string;
  };
}

export type GoalsBarType = 'sequential' | 'inline' | 'single_bar';
export type GoalsMinimumThresholdType = 'cart_value' | 'item_count';
export type GoalsLabelPosition = 'above_bar' | 'below_bar';
export type GoalsPillSource = 'first' | 'second';
export type GoalsPadding = 'none' | 'small' | 'medium' | 'large';

export type GoalOfferType = 'freeShipping' | 'orderDiscount' | 'product';
export type GoalImageFit = 'square' | 'crop' | 'fill';
export type GoalGiftMode = 'single' | 'multiple';
export type GoalProductDisplayType = 'list' | 'compact';

export interface GoalPreviewProduct {
  name: string;
  price?: string;
  imageUrl?: string;
}

export interface GoalApplySectionPreview {
  background: {
    color: string;
    padding: GoalsPadding;
    cornerRadius: number;
    border: { enabled: boolean; color: string; width: number };
  };
  texts: { title: string; subtitle: string; textColor: string; fontSize: number };
  // null design fields inherit from the global button design.
  button: { label: string; backgroundColor: string | null; textColor: string | null; cornerRadius: number | null };
  image: { fit: GoalImageFit; borderWidth: number; cornerRadius: number; innerPadding: number };
}

export interface GoalPreviewAppearance {
  iconUrl: string | File;
  // Padding around whichever of the goal's two messages is showing. Absent on older configs.
  messagePadding?: GoalsPadding;
  firstLabel: string;
  firstLabelPosition: GoalsLabelPosition;
  secondLabel: string;
  secondLabelPosition: GoalsLabelPosition;
  showLabelsInProgressBar: boolean;
  labelSize: number;
  pillSource: GoalsPillSource;
  pillText: string;
  applySection: GoalApplySectionPreview;
}

export interface GoalPreviewSetting {
  enabled: boolean;
  thresholds: { threshold: number; currency: string }[];
  messageWhenNotMet: string;
  messageWhenMet: string;
  offer: {
    type: GoalOfferType;
    product: { giftMode: GoalGiftMode; productDisplayType: GoalProductDisplayType; products: GoalPreviewProduct[] };
  };
  appearance: GoalPreviewAppearance;
}

export type GoalsTimerFormat = 'digital' | 'compact';
export type GoalsTimerPlacement = 'inline' | 'badge' | 'in_goal_message';

export interface TimerStatePreview {
  message: string;
  backgroundColor: string;
  textColor: string;
  iconUrl: string | File;
}

export interface GoalsTimerPreview {
  enabled: boolean;
  format: GoalsTimerFormat;
  placement: GoalsTimerPlacement;
  defaultMessage: TimerStatePreview;
  lowTimer: TimerStatePreview;
  expiration: TimerStatePreview;
}

export interface GoalsProgressBarSettings {
  minimumThresholdType: GoalsMinimumThresholdType;
  timer?: GoalsTimerPreview;
  appearance: {
    barType: GoalsBarType;
    // Rule under the whole widget. Absent on carts saved before the option existed.
    bottomBorder?: {
      enabled: boolean;
      color: string;
    };
    progressBar: {
      color: string;
      barWidth: number;
      cornerRadius: number;
    };
    pills: {
      enabled: boolean;
      backgroundColor: string;
      textColor: string;
      cornerRadius: number;
    };
    background: {
      color: string;
      padding: GoalsPadding;
      cornerRadius: number;
      border: {
        enabled: boolean;
        color: string;
        width: number;
      };
    };
  };
  goals: GoalPreviewSetting[];
}

export type UpsellTimerPlacement = 'inline' | 'badge' | 'in_title';

export interface UpsellTimerPreview {
  enabled: boolean;
  format: TimerFormat;
  placement: UpsellTimerPlacement;
  defaultMessage: TimerStatePreview;
  lowTimer: TimerStatePreview;
  expiration: TimerStatePreview;
}

export interface UpsellAdditionalDisplayText {
  type: string;
  display: boolean;
  text: string;
  priority: number;
}

// A Design box (Main Background / Product Item / Image): a color OR image fit, + border/corner/padding.
export interface UpsellDesignBox {
  color?: string;
  fit?: string;
  borderWidth?: number;
  cornerRadius?: number;
  innerPadding?: number;
}

// Subset of the Upsell form's getRawValue() that the admin preview renders.
export interface UpsellItemsSettings {
  productSelection?: {
    settings?: {
      sectionTitle?: string;
      /** Empty or absent inherits the theme's colour, mirroring the storefront. */
      sectionTitleColor?: string;
      textAddToOrder?: string;
      buttonBackgroundColor?: string | null;
      buttonTextColor?: string | null;
      buttonCornerRadius?: number | null;
      additionalDisplayTexts?: UpsellAdditionalDisplayText[];
      // 'inside' = Always Show (inline dropdown) | 'outside' = Popup (opens from the CTA).
      variantSelector?: string;
      showCompareAtPrice?: boolean;
    };
    style?: {
      // CheckoutUpsellExtension.DisplayType: 'cards' (Carousel) | 'row' (List) | 'compact'.
      displayType?: string;
      // CheckoutUpsellExtension.CarouselNavigationStyle: 'arrows' | 'scroll' (Carousel only).
      carouselNavigationStyle?: string;
      // Rows shown per view in List display (arrows paginate the rest).
      numberOfRowsInRowDisplayType?: number;
      // Design boxes — color/fit + border/corner/padding, applied live in the preview.
      mainBackground?: UpsellDesignBox;
      productItem?: UpsellDesignBox;
      image?: UpsellDesignBox;
    };
  };
  timer?: UpsellTimerPreview;
}

export type DigitalUpsellSelectingMethodType = 'toggle' | 'checkbox';
export type DigitalUpsellImageFit = 'contain' | 'cover';

export interface DigitalUpsellCard {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  borderWidth: number;
  cornerRadius: number;
  innerPadding: number;
}

export interface DigitalUpsellTimerPreview {
  enabled: boolean;
  format: TimerFormat;
  placement: 'inline' | 'badge';
  defaultMessage: TimerStatePreview;
  lowTimer: TimerStatePreview;
  expiration: TimerStatePreview;
}

export interface DigitalUpsellSettings {
  title: string;
  description: string;
  pricingMethod: 'fixed' | 'percent';
  flatPrice: number;
  percent: {
    minPrice: number;
    maxPrice: number;
    cartPercentage: number;
  };
  productImageBase64: string;
  appearance: {
    card: DigitalUpsellCard;
    image: {
      fit: DigitalUpsellImageFit;
      cornerRadius: number;
    };
  };
  tooltip: {
    enabled: boolean;
    content: string;
    appearance: {
      backgroundColor: string;
      borderWidth: number;
      cornerRadius: number;
      innerPadding: number;
    };
  };
  selectingMethod: {
    type: DigitalUpsellSelectingMethodType;
    defaultColor: string;
    selectedColor: string;
  };
  timer?: DigitalUpsellTimerPreview;
}

export interface CheckoutAreaSettings {
  terms: {
    enabled: boolean;
    text: string;
    /** Absent falls back to the colour the row has always used, mirroring the storefront. */
    textColor?: string;
  };
  checkoutButton: {
    enabled: boolean;
    label: string;
    design: CheckoutButtonDesign;
    loaderLabel: string;
    routing: 'automatic' | 'custom_url';
    customUrl: string;
  };
  viewCartButton: {
    enabled: boolean;
    label: string;
    design: CheckoutButtonDesign;
    loaderLabel: string;
  };
  continueShoppingButton: {
    enabled: boolean;
    label: string;
  };
  checkoutMethods: {
    expressCheckout: boolean;
  };
  customCode: {
    enabled: boolean;
    code: string;
  };
}
